import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const DURATION = 7000;

export function HeroCarousel() {
  const { content, lang } = useLang();
  const slides = content.insights || [];
  const [index, setIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const goTo = useCallback((i) => { setProgress(0); setIndex(i); }, []);

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const started = Date.now();
    const tick = setInterval(() => setProgress(Math.min((Date.now() - started) / DURATION, 1)), 120);
    const timer = setTimeout(() => goTo((index + 1) % slides.length), DURATION);
    return () => { clearInterval(tick); clearTimeout(timer); };
  }, [index, slides.length, goTo]);

  if (!slides.length) return <section data-testid="hero-section" className="h-[60svh] bg-black" />;

  const slide = slides[Math.min(index, slides.length - 1)];

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[620px] w-full overflow-hidden bg-black">
      <motion.div style={{ y }} className="absolute inset-0">
        <AnimatePresence mode="sync">
          <motion.img
            key={slide.slug}
            src={slide.image}
            alt=""
            data-testid={`hero-image-${slide.slug}`}
            className="absolute inset-0 h-full w-full object-cover"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 1 }, scale: { duration: 8, ease: "linear" } }}
          />
        </AnimatePresence>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/25" />

      <motion.div style={{ opacity }} className="container-x relative z-10 flex h-full flex-col justify-end pb-12 md:pb-16">
        <AnimatePresence mode="wait">
          <motion.div
            key={slide.slug}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h2 data-testid={`hero-title-${slide.slug}`} className="font-pov text-3xl sm:text-4xl lg:text-5xl text-white">{slide.title}</h2>
            <Link
              to={`/points-de-vue/${slide.slug}`}
              data-testid={`hero-learn-more-${slide.slug}`}
              className="mt-8 inline-flex items-center gap-2 text-sm text-white transition-opacity duration-300 hover:opacity-70"
            >
              {content.ui.learnMore} <ArrowRight size={16} />
            </Link>
          </motion.div>
        </AnimatePresence>

        <div className="mt-12 flex items-center gap-4" data-testid="hero-slide-indicators">
          <div className="grid flex-1 gap-3" style={{ gridTemplateColumns: `repeat(${slides.length}, minmax(0, 1fr))` }}>
            {slides.map((s, i) => (
              <button
                key={s.slug}
                data-testid={`hero-indicator-${i}`}
                onClick={() => goTo(i)}
                className="py-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
                aria-label={`${lang === "fr" ? "Point de vue" : "Insight"} ${i + 1}`}
                aria-current={i === index}
              >
                <span className="relative block h-px w-full overflow-hidden bg-white/30">
                  {i === index && <span data-testid="hero-active-progress" className="absolute inset-0 origin-left bg-white transition-transform duration-150 ease-linear" style={{ transform: `scaleX(${progress})` }} />}
                  {i < index && <span className="absolute inset-0 bg-white/60" />}
                </span>
              </button>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
